// Placeholder catalog. Codes stand in until the real CPT, HCPCS, and revenue mapping is settled.
// The services page reads and writes this list in the browser. DynamoDB comes later.
// Rules and humanDescription here are the default adjudication. A plan can replace them for itself.

const KEY = "internal-services-v7";

const group = (name, from, to) => ({ name, from, to });
const rule = (join, when, then) => (join === "else" ? { join, then } : { join, when, then });

export const PCP_TAXONOMY = [
    "261QP2300X",
    "207Q00000X",
    "207QA0505X",
    "207R00000X",
    "208000000X",
    "208D00000X",
    "363A00000X",
    "363LF0000X",
    "363LA2200X",
    "363LP0200X",
    "363LP2300X",
];

export const TAXONOMY_MISMATCH = [
    { value: "routeToSickVisit", label: "Pay as a PCP visit", description: "The member pays the PCP copay." },
    { value: "routeToSpecialist", label: "Pay as a specialist visit", description: "The member pays the specialist copay." },
    { value: "processAsBaseSpecialist", label: "Pay the regular specialist amount", description: "The member pays the standard specialist copay." },
    { value: "pendForCredentialReview", label: "Hold for a license check", description: "Someone checks the provider before the claim pays." },
    { value: "deny", label: "Deny it", description: "This provider type can't bill this service." },
    { value: "continue", label: "Pay it the same way", description: "The provider type makes no difference." },
];

export const TYPE_OF_BILL = {
    "0111": "Inpatient hospital, admit through discharge",
    "0112": "Inpatient hospital, first interim claim",
    "0131": "Outpatient hospital",
    "0141": "Hospital, non-patient (reference lab)",
    "0321": "Home health",
    "0711": "Rural health clinic",
    "0721": "Dialysis facility (ESRD)",
    "0722": "Dialysis facility, first interim claim",
    "0771": "Federally qualified health center",
    "0811": "Hospice, non-hospital",
    "0831": "Hospital outpatient ambulatory surgery",
    "0851": "Critical access hospital",
};

export const SERVICE_CATALOG = [
    {
        key: "preventiveCare",
        cptCodeGroups: [group("preventiveCareCptCodes", "99381", "99397")],
        cptCodes: ["99401", "99402", "99403", "99404"],
        hcpcsCodes: ["G0438", "G0439"],
        placesOfService: ["11", "22"],
        taxonomyCodes: PCP_TAXONOMY,
        taxonomyMismatch: "routeToSickVisit",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: false,
        rules: [
            rule("if", "The doctor is a primary care provider and the member is due for this checkup", "The plan pays it all. The member pays $0, even before the deductible."),
            rule("else", "", "Pay it as a regular PCP visit."),
        ],
        humanDescription: "Yearly checkups and wellness visits. The member pays $0. The visit has to be with a family doctor, internal medicine doctor, pediatrician, or preventive medicine doctor.",
    },
    {
        key: "pcpVisit",
        cptCodeGroups: [group("pcpVisitCptCodes", "99202", "99215")],
        revenueCodeGroups: [group("pcpVisitRevenueCodes", "0510", "0519")],
        revenueCode: ["0521"],
        placesOfService: ["11", "22"],
        typeOfBill: ["0131", "0711", "0771"],
        taxonomyCodes: PCP_TAXONOMY,
        taxonomyMismatch: "routeToSpecialist",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The bill comes from the clinic or hospital where the visit happened", "Charge the member the PCP copay, the same as the doctor's bill."),
            rule("elseIf", "The doctor is a primary care provider", "Charge the member the PCP copay."),
            rule("else", "", "Pay it as a specialist visit."),
        ],
        humanDescription: "A sick or follow-up visit with a family doctor, internal medicine doctor, or pediatrician. PCPs and specialists use the same visit codes, so the doctor's type decides which copay the member pays. Some clinics send a second bill for the building and staff. That bill also pays at the PCP level.",
    },
    {
        key: "specialistVisit",
        cptCodeGroups: [
            group("specialistVisitCptCodes", "99202", "99215"),
            group("specialistConsultCptCodes", "99242", "99245"),
        ],
        revenueCodeGroups: [group("specialistVisitRevenueCodes", "0510", "0519")],
        placesOfService: ["11", "22"],
        typeOfBill: ["0131"],
        taxonomyMismatch: "processAsBaseSpecialist",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The plan needs a referral and the member has none", "Charge the no-referral penalty. When the plan has no penalty, deny the visit."),
            rule("else", "", "Charge the member the specialist copay."),
        ],
        humanDescription: "A visit with a specialist, such as a cardiologist or dermatologist. Some plans need a referral from the PCP first.",
    },
    {
        key: "obgynVisit",
        cptCodeGroups: [group("obgynVisitCptCodes", "99202", "99215")],
        placesOfService: ["11", "22"],
        taxonomyCodes: ["207V00000X", "207VG0400X", "207VX0000X", "363LW0102X", "367A00000X"],
        taxonomyMismatch: "routeToSpecialist",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The doctor is an OB/GYN or a midwife", "Charge the member the OB/GYN copay. A referral is never needed."),
            rule("else", "", "Pay it as a specialist visit."),
        ],
        humanDescription: "A visit with an OB/GYN or a midwife. Members can go straight to OB/GYN care.",
    },
    {
        key: "maternityProfessional",
        cptCodes: ["59400", "59510", "59610", "59618"],
        placesOfService: ["11", "21", "22"],
        taxonomyCodes: ["207V00000X", "367A00000X"],
        taxonomyMismatch: "routeToSpecialist",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The doctor bills one fee for the whole pregnancy", "Pay the fee once. Checkups before and after the birth are included, so they pay $0."),
        ],
        humanDescription: "The doctor's fee for a pregnancy. One bill covers checkups during pregnancy, the delivery, and the checkup after the birth.",
    },
    {
        key: "maternityFacility",
        revenueCode: ["0112", "0122", "0720", "0721", "0722"],
        typeOfBill: ["0111", "0112"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The mother goes home within 2 days of a vaginal birth, or within 4 days of a C-section", "Cover the stay. No approval is needed."),
            rule("else", "", "The extra days need approval, the same as any hospital stay."),
        ],
        humanDescription: "The hospital bill for labor and delivery. Federal law covers 2 days after a vaginal birth and 4 days after a C-section with no approval needed. Add the baby to the plan.",
    },
    {
        key: "behavioralHealthOffice",
        cptCodes: ["90791", "90792", "90832", "90834", "90837"],
        revenueCode: ["0513", "0914"],
        placesOfService: ["11", "53"],
        typeOfBill: ["0131"],
        taxonomyCodes: ["103T00000X", "103TC0700X", "1041C0700X", "101YM0800X", "106H00000X", "2084P0800X", "363LP0808X"],
        taxonomyMismatch: "pendForCredentialReview",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The provider is not a licensed mental health provider", "Hold the claim until someone checks the provider's license."),
            rule("else", "", "Charge the member the mental health copay. It can be no higher than a regular doctor visit."),
        ],
        humanDescription: "Therapy and psychiatry visits. Federal law says mental health care can cost the member no more than the matching medical care.",
    },
    {
        key: "intensiveOutpatient",
        hcpcsCodes: ["H0015", "H0035", "S9480"],
        revenueCode: ["0905", "0906", "0912", "0913"],
        placesOfService: ["52", "53"],
        typeOfBill: ["0131", "0851"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The visit has no approval", "Hold the claim until it is approved."),
            rule("else", "", "Charge the member the daily copay."),
        ],
        humanDescription: "Day programs for mental health or substance use care. The member goes for several hours a day and sleeps at home. Paid per day. It needs approval first.",
    },
    {
        key: "outpatientRehabilitation",
        cptCodeGroups: [
            group("rehabEvaluationCptCodes", "97161", "97168"),
            group("rehabTherapyCptCodes", "97110", "97546"),
        ],
        revenueCodeGroups: [group("outpatientRehabilitationRevenueCodes", "0420", "0449")],
        placesOfService: ["11", "22"],
        typeOfBill: ["0131"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The member has used all the visits the plan allows", "Deny the visit. The limit is reached."),
            rule("else", "", "Charge the member the therapy copay."),
        ],
        humanDescription: "Physical, occupational, and speech therapy.",
    },
    {
        key: "chiropractice",
        cptCodeGroups: [group("chiropracticeCptCodes", "98940", "98942")],
        revenueCode: ["0940"],
        placesOfService: ["11"],
        typeOfBill: ["0131"],
        taxonomyCodes: ["111N00000X"],
        taxonomyMismatch: "deny",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The provider is not a chiropractor", "Deny the visit."),
            rule("elseIf", "The member has used all the visits the plan allows", "Deny the visit. The limit is reached."),
            rule("elseIf", "The care only keeps the member where they are, with no expected improvement", "Deny the visit."),
            rule("else", "", "Charge the member the chiropractic copay."),
        ],
        humanDescription: "Spine adjustments by a chiropractor. The plan covers care that helps the member get better.",
    },
    {
        key: "acupuncture",
        cptCodeGroups: [group("acupunctureCptCodes", "97810", "97814")],
        revenueCode: ["0940"],
        placesOfService: ["11"],
        typeOfBill: ["0131"],
        taxonomyCodes: ["171100000X", "111N00000X", "207Q00000X", "207R00000X"],
        taxonomyMismatch: "deny",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The bill has extra 15-minute time with no first 15 minutes on the same day", "Deny the extra time."),
            rule("elseIf", "The member has used all the visits the plan allows", "Deny the visit. The limit is reached."),
            rule("else", "", "Charge the member the acupuncture copay."),
        ],
        humanDescription: "Acupuncture visits. Extra time only pays along with the first 15 minutes on the same day. Dry needling is a separate service.",
    },
    {
        key: "basiclabs",
        cptCodes: ["36415"],
        cptCodeGroups: [group("basiclabsCptCodes", "80047", "89398")],
        revenueCodeGroups: [group("basiclabsRevenueCodes", "0300", "0319")],
        placesOfService: ["11", "19", "22", "81"],
        typeOfBill: ["0131", "0141"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The lab is done the same day as a PCP visit", "Charge the member the \"With PCP visit\" amount."),
            rule("elseIf", "The lab is done the same day as an OB/GYN visit", "Charge the member the \"With OB/GYN visit\" amount."),
            rule("elseIf", "The lab is done the same day as a specialist visit", "Charge the member the \"With specialist visit\" amount."),
            rule("else", "", "Charge the member the regular lab amount."),
        ],
        humanDescription: "Blood tests and other lab work.",
    },
    {
        key: "basicDiagnostic/radiology",
        cptCodes: ["71046"],
        revenueCodeGroups: [
            group("basicRadiologyRevenueCodes", "0320", "0329"),
            group("ultrasoundRevenueCodes", "0400", "0409"),
        ],
        placesOfService: ["11", "19", "22"],
        typeOfBill: ["0131"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The X-ray or ultrasound is done the same day as a PCP visit", "Charge the member the \"With PCP visit\" amount."),
            rule("elseIf", "The X-ray or ultrasound is done the same day as a specialist visit", "Charge the member the \"With specialist visit\" amount."),
            rule("else", "", "Charge the member the regular imaging amount."),
        ],
        humanDescription: "X-rays and ultrasounds. One test can come as two bills: one for the machine and one for the doctor who reads it.",
    },
    {
        key: "basicLabImaging",
        cptCodes: ["80053"],
        revenueCode: ["0300"],
        placesOfService: ["11", "22", "81"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The test is done the same day as a PCP visit", "Charge the member the \"With PCP visit\" amount."),
            rule("elseIf", "The test is done the same day as a specialist visit", "Charge the member the \"With specialist visit\" amount."),
            rule("else", "", "Charge the member the regular amount."),
        ],
        humanDescription: "Common lab tests and simple imaging. The member can pay less when the test is done the same day as a doctor visit.",
    },
    {
        key: "advanceDiagnostic",
        cptCodeGroups: [
            group("ctMriHeadNeckCptCodes", "70450", "70559"),
            group("ctMriChestCptCodes", "71250", "71555"),
            group("ctMriSpineCptCodes", "72125", "72159"),
            group("ctMriAbdomenCptCodes", "74150", "74183"),
            group("petCptCodes", "78811", "78816"),
        ],
        revenueCodeGroups: [
            group("ctRevenueCodes", "0350", "0359"),
            group("mriRevenueCodes", "0610", "0619"),
        ],
        revenueCode: ["0404"],
        placesOfService: ["11", "19", "22", "24"],
        typeOfBill: ["0131"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The scan has no approval", "Hold the claim until it is approved."),
            rule("else", "", "Charge the member the advanced imaging amount."),
        ],
        humanDescription: "CT, MRI, and PET scans. These need approval first.",
    },
    {
        key: "outpatientSurgery",
        cptCodeGroups: [group("outpatientSurgeryCptCodes", "10004", "69990")],
        revenueCodeGroups: [group("outpatientSurgeryRevenueCodes", "0360", "0369")],
        revenueCode: ["0490"],
        placesOfService: ["22", "24"],
        typeOfBill: ["0131", "0831"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The surgery has no approval", "Hold the claim until it is approved."),
            rule("else", "", "Charge the member the outpatient surgery amount."),
        ],
        humanDescription: "Surgery where the member goes home the same day, at a hospital or a surgery center. A second surgery on the same day costs less, and the pricing system handles that.",
    },
    {
        key: "globalSurgeryFollowUp",
        cptCodes: ["99024"],
        placesOfService: ["11"],
        countsTowardOutOfPocket: false,
        rules: [
            rule("if", "The visit is in the follow-up window after a surgery", "The plan pays $0 and the member pays $0. The surgery fee already covers it."),
        ],
        humanDescription: "A checkup after surgery. The surgery fee already covers it.",
    },
    {
        key: "anesthesia",
        cptCodeGroups: [group("anesthesiaCptCodes", "00100", "01999")],
        revenueCode: ["0370", "0374"],
        placesOfService: ["21", "22", "24"],
        typeOfBill: ["0111", "0131"],
        taxonomyCodes: ["207L00000X", "367500000X", "367H00000X"],
        taxonomyMismatch: "pendForCredentialReview",
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The provider is not an anesthesiologist or a nurse anesthetist", "Hold the claim until someone checks the provider's license."),
            rule("else", "", "Charge the member the anesthesia amount."),
        ],
        humanDescription: "Anesthesia during a procedure. The pricing system sets the price from the procedure and the time. The plan sets what the member pays.",
    },
    {
        key: "inpatientHospital",
        revenueCodeGroups: [
            group("roomAndBoardRevenueCodes", "0100", "0169"),
            group("intensiveCareRevenueCodes", "0200", "0219"),
        ],
        typeOfBill: ["0111", "0112"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The hospital stay has no approval", "Charge the missing-approval penalty to the person the plan names."),
            rule("else", "", "Charge the member the hospital stay amount for the approved days."),
        ],
        humanDescription: "An overnight hospital stay. The plan checks the days against the approval.",
    },
    {
        key: "urgentCare",
        cptCodeGroups: [group("urgentCareCptCodes", "99202", "99215")],
        hcpcsCodes: ["S9083", "S9088"],
        revenueCode: ["0456"],
        placesOfService: ["20"],
        typeOfBill: ["0131"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The visit is at an urgent care center", "Charge the member the urgent care copay."),
        ],
        humanDescription: "A walk-in visit at an urgent care center, for care that can't wait but is not an emergency.",
    },
    {
        key: "emergencyRoom",
        cptCodeGroups: [group("emergencyRoomCptCodes", "99281", "99285")],
        cptCodes: ["99291"],
        revenueCode: ["0450", "0451", "0452", "0459"],
        placesOfService: ["23"],
        typeOfBill: ["0131", "0141"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The member is admitted to the hospital from the ER", "Waive the ER copay. Pay the stay as a hospital stay."),
            rule("elseIf", "The symptoms would make a reasonable person think it was an emergency", "Charge the member the ER copay. Out-of-network ERs pay at the in-network level."),
            rule("else", "", "Charge the member the non-emergency ER amount and penalty."),
        ],
        humanDescription: "A visit to the emergency room. The plan judges an emergency by the symptoms the member had, and the final diagnosis doesn't change that. Out-of-network ERs pay at the in-network level.",
    },
    {
        key: "homeHealthCare",
        hcpcsCodeGroups: [group("homeHealthCareHcpcsCodes", "G0151", "G0162")],
        revenueCodeGroups: [group("homeHealthCareRevenueCodes", "0550", "0559")],
        placesOfService: ["12"],
        typeOfBill: ["0321"],
        taxonomyCodes: ["251E00000X"],
        taxonomyMismatch: "deny",
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The member can leave home without major effort", "Deny the visit. Home care is for members who can't leave home."),
            rule("elseIf", "The member has used all the home visits the plan allows", "Deny the visit. The limit is reached."),
            rule("else", "", "Charge the member the home health amount."),
        ],
        humanDescription: "Nursing and therapy at home from a home health agency, for members who can't easily leave home.",
    },
    {
        key: "hospiceProfessionalCare",
        revenueCodeGroups: [group("hospiceProfessionalCareRevenueCodes", "0651", "0659")],
        typeOfBill: ["0811"],
        taxonomyCodes: ["251G00000X"],
        taxonomyMismatch: "deny",
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The member has chosen hospice care", "The plan pays hospice a daily rate. The member pays the plan's hospice amount."),
        ],
        humanDescription: "Comfort care at the end of life from a hospice agency, paid per day.",
    },
    {
        key: "wigs",
        hcpcsCodes: ["A9282"],
        countsTowardOutOfPocket: true,
    },
    {
        key: "groundAmbulance",
        hcpcsCodes: ["A0426", "A0427", "A0428", "A0429", "A0433", "A0434", "A0999"],
        revenueCode: ["0540", "0546"],
        placesOfService: ["41"],
        typeOfBill: ["0131", "0851"],
        taxonomyCodes: ["341600000X", "3416L0300X", "3416S0300X"],
        taxonomyMismatch: "deny",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The ride is billed with the general code A0999, such as a boat ambulance", "Hold the claim until someone reviews the trip."),
            rule("elseIf", "The ride is not an emergency and has no approval", "Deny the ride."),
            rule("elseIf", "The ambulance is out of network and state law protects the member", "Charge the member the in-network amount. The ambulance can't bill the member for more."),
            rule("else", "", "Charge the member the ground ambulance amount."),
        ],
        humanDescription: "Rides by ground ambulance, including the rare boat ambulance. Emergency rides are always covered. Rides that are not an emergency, like a transfer between hospitals, need approval first. Some states protect members from surprise bills on out-of-network ground rides.",
    },
    {
        key: "groundAmbulanceMileage",
        hcpcsCodes: ["A0425"],
        revenueCode: ["0540"],
        placesOfService: ["41"],
        typeOfBill: ["0131", "0851"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "There is no ground ambulance ride on the same day", "Deny the miles."),
            rule("else", "", "Pay the miles at the ground per-mile rate."),
        ],
        humanDescription: "The miles a ground ambulance drives. Miles only pay with a ground ride on the same day.",
    },
    {
        key: "airAmbulance",
        hcpcsCodes: ["A0430", "A0431"],
        revenueCode: ["0545"],
        placesOfService: ["42"],
        typeOfBill: ["0131", "0851"],
        taxonomyCodes: ["341600000X", "3416A0800X"],
        taxonomyMismatch: "deny",
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The flight is not an emergency and has no approval", "Deny the flight."),
            rule("elseIf", "The air ambulance is out of network", "Charge the member the in-network amount. The air ambulance can't bill the member for more."),
            rule("else", "", "Charge the member the air ambulance amount."),
        ],
        humanDescription: "Rides by airplane or helicopter ambulance. Federal law protects members from surprise bills on every air ambulance ride, in or out of network. Flights that are not an emergency need approval first.",
    },
    {
        key: "airAmbulanceMileage",
        hcpcsCodes: ["A0435", "A0436"],
        revenueCode: ["0545"],
        placesOfService: ["42"],
        typeOfBill: ["0131", "0851"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "There is no air ambulance ride on the same day", "Deny the miles."),
            rule("else", "", "Pay the miles at the air per-mile rate."),
        ],
        humanDescription: "The miles an air ambulance flies. A0435 is airplane miles and A0436 is helicopter miles. Miles only pay with an air ride on the same day.",
    },
    {
        key: "specialtyDrugs",
        hcpcsCodeGroups: [group("specialtyDrugsHcpcsCodes", "J0120", "J9999")],
        hcpcsCodes: ["J3490", "J3590"],
        revenueCode: ["0250", "0636"],
        placesOfService: ["11", "12", "19", "22"],
        typeOfBill: ["0131"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The drug is billed with a general code for drugs with no code of their own", "Hold the claim until the drug name and the invoice come in."),
            rule("elseIf", "The drug has no approval", "Deny the drug."),
            rule("else", "", "Charge the member the specialty drug amount."),
        ],
        humanDescription: "Drugs given by shot or IV at a clinic, such as infusions. Drugs from a pharmacy use the Rx benefit.",
    },
    {
        key: "preventiveScreenings",
        cptCodes: ["45378", "45380", "77067", "77063"],
        hcpcsCodes: ["G0105", "G0121"],
        revenueCode: ["0403", "0750"],
        placesOfService: ["11", "22", "24"],
        typeOfBill: ["0131", "0831"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: false,
        rules: [
            rule("if", "The test is a screening", "The plan pays it all. The member pays $0, even before the deductible."),
            rule("else", "", "Pay it as a diagnostic test."),
        ],
        humanDescription: "Screening colonoscopies and mammograms. A polyp removed during a screening colonoscopy still counts as a screening.",
    },
    {
        key: "radiationTherapy",
        cptCodeGroups: [
            group("radiationTherapyCptCodes", "77261", "77799"),
            group("chemotherapyCptCodes", "96401", "96549"),
        ],
        revenueCode: ["0330", "0333", "0335"],
        placesOfService: ["11", "19", "22"],
        typeOfBill: ["0131"],
        taxonomyCodes: ["2085R0001X", "207RX0202X", "207RH0003X"],
        taxonomyMismatch: "pendForCredentialReview",
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The treatment has no approval", "Hold the claim until it is approved."),
            rule("else", "", "Charge the member the cancer treatment amount."),
        ],
        humanDescription: "Radiation and chemotherapy where the member goes home the same day. These need approval first.",
    },
    {
        key: "dialysis",
        cptCodeGroups: [group("dialysisCptCodes", "90935", "90999")],
        revenueCodeGroups: [group("dialysisRevenueCodes", "0820", "0889")],
        typeOfBill: ["0721", "0722"],
        countsTowardOutOfPocket: true,
        rules: [
            rule("if", "The member has kidney failure and has been on Medicare for more than 30 months", "Medicare pays first. The plan pays second."),
            rule("else", "", "The plan pays first. Charge the member the dialysis amount."),
        ],
        humanDescription: "Kidney dialysis. For a member with kidney failure, the plan pays first for the first 30 months of Medicare. After that, Medicare pays first. The plan gives these members the same benefits as everyone else.",
    },
    {
        key: "virtualUrgentCare",
        cptCodeGroups: [group("virtualVisitCptCodes", "98000", "98016")],
        placesOfService: ["02", "10"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        humanDescription: "A video or phone visit with a doctor.",
    },
    {
        key: "virtualBehavioralHealth",
        cptCodes: ["90791", "90832", "90834", "90837"],
        placesOfService: ["02", "10"],
        maxDailyUnits: 1,
        countsTowardOutOfPocket: true,
        humanDescription: "Therapy by video or phone.",
    },
];

export const serviceLabel = (key) => String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase())
    .replace(/\bPcp\b/g, "PCP")
    .replace(/\bHcpcs\b/g, "HCPCS")
    .replace(/\bCpt\b/g, "CPT");

export const codeSummary = (codes = [], groups = []) => [
    ...codes,
    ...groups.map((group) => `${group.from}–${group.to}`),
].filter(Boolean).join(", ");

export function loadServices() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (raw) return JSON.parse(raw);
    } catch {
        // A bad session value falls through to the seed catalog.
    }
    const services = structuredClone(SERVICE_CATALOG);
    sessionStorage.setItem(KEY, JSON.stringify(services));
    return services;
}

export function saveServices(services) {
    sessionStorage.setItem(KEY, JSON.stringify(services));
}
